import React, { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { Col, Row, Spin } from 'antd';
import pluralize from 'pluralize';

import ContractorCompanyCard from './ContractorCompanyCard';
import { ContractorCompanyItemViewModel } from 'api/backofficeApiClient';
import { contractorCompanyCreatedAtSelector } from 'redux/selectors/contractorCompanySelectors';
import { getContractorCompaniesForCurrentMember } from 'redux/reducers/contractorCompanySlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

export default function ContractorCompaniesSection() {
  const dispatch = useAppDispatch();
  const contractorCompanyCreatedAt = useAppSelector(
    contractorCompanyCreatedAtSelector,
  );
  const [contractorCompanies, setContractorCompanies] = useState<
    ContractorCompanyItemViewModel[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const infiniteScrollParentRef = useRef(null);
  const companiesTotalCount = pluralize('contractor company', totalCount, true);

  useEffect(() => {
    initInfinityScroll();
  }, [contractorCompanyCreatedAt]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setContractorCompanies([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetContractorCompanies = useCallback(
    (result) => {
      const loadedContractorCompanies = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newContractorCompanies = [
        ...contractorCompanies,
        ...loadedContractorCompanies,
      ];
      const hasMoreRows = newContractorCompanies.length < totalCount;

      setHasMore(hasMoreRows);
      setContractorCompanies(newContractorCompanies);
      setTotalCount(totalCount);
    },
    [contractorCompanies],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const result = await dispatch(
        getContractorCompaniesForCurrentMember(pageNumber),
      );

      if (getContractorCompaniesForCurrentMember.fulfilled.match(result)) {
        onSetContractorCompanies(result);
      } else {
        setHasMore(false);
      }

      setIsInitialDataLoaded(true);
    },
    [dispatch, onSetContractorCompanies],
  );

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          {isInitialDataLoaded && (
            <>
              {!!totalCount && (
                <p className="page-total-count">{companiesTotalCount}</p>
              )}

              {!totalCount && (
                <span className="no-data-msg">
                  You have no contractor companies. Perhaps it is time to create
                  a new one? Let us know if we can help.
                </span>
              )}
            </>
          )}

          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {contractorCompanies.map((company, index) => (
                  <Col
                    md={24}
                    lg={12}
                    className="d-flex"
                    key={`${company.id}-${index}`}
                  >
                    <ContractorCompanyCard company={company} />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
